/*
 * FirstUploadNudge — Stage-13 launch readiness sprint (P1.2).
 * Day-1 magic-moment driver. Auto-hides as soon as the user has uploaded
 * their first document, or after they explicitly dismiss it.
 *
 * Lives high on Home for new users so the very first action they're nudged
 * toward is the one that unlocks the lifecycle conversion case (uploaded
 * report → asset → switching cost → trial→paid).
 *
 * Honest framing: no fake personalisation, no fake memory claims. Just
 * "drop a report, we'll read it" — the cleanest possible commitment.
 */
import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { UploadSimple, X, Sparkle } from '@phosphor-icons/react';
import axios, { API } from '../../services/api';
import { peekSWRCache, warmSWRCache } from '../../services/useSWRCache';
import { track } from '../../services/activation';

const STORAGE_KEY = 'kindra:first-upload-nudge:dismissed';

export const FirstUploadNudge = () => {
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Honour explicit dismissal.
      try {
        if (localStorage.getItem(STORAGE_KEY) === '1') return;
      } catch {
        /* private browsing — proceed */
      }

      // P0 Stage-14b — cache-first peek. If we already know the user's doc
      // count (e.g. an upload just completed and warmed the cache), use it
      // synchronously: hide the nudge instantly, no flash of "Upload one
      // report..." appearing then disappearing after a fetch.
      const cached = peekSWRCache('/account/value-summary');
      if (cached && Number(cached.documents || 0) > 0) {
        return; // user already has docs — no nudge.
      }
      if (cached && Number(cached.documents || 0) === 0) {
        setShow(true);
        try { track('first_upload_nudge_shown', { surface: 'home' }); } catch { /* noop */ }
        // Still revalidate in the background in case it's stale.
      }

      try {
        const { data } = await axios.get(`${API}/account/value-summary`);
        if (cancelled) return;
        warmSWRCache('/account/value-summary', data);
        const docs = Number(data?.documents || 0);
        if (docs === 0 && !cached) {
          setShow(true);
          try { track('first_upload_nudge_shown', { surface: 'home' }); } catch { /* noop */ }
        } else if (docs > 0) {
          // Auto-hide if revalidation reveals an upload already happened.
          setShow(false);
        }
      } catch {
        /* silent — never block home */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleStart = () => {
    try { track('first_upload_nudge_clicked', { surface: 'home' }); } catch { /* noop */ }
    navigate('/documents');
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    try { track('first_upload_nudge_dismissed', { surface: 'home' }); } catch { /* noop */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="relative rounded-2xl border border-[#E6DEC9] bg-gradient-to-br from-[#FAF7EF] to-white p-5 cursor-pointer hover:border-[#CFC8BB] transition-colors"
      onClick={handleStart}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleStart(); } }}
      data-testid="first-upload-nudge"
    >
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
        aria-label="Dismiss"
        data-testid="first-upload-nudge-dismiss"
      >
        <X size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        <div className="shrink-0 w-10 h-10 rounded-xl bg-[#235347]/10 flex items-center justify-center">
          <UploadSimple size={20} className="text-[#235347]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.18em] uppercase text-slate-400 font-medium">
            Start here · the magic moment
          </p>
          <h3
            className="text-[20px] text-slate-900 leading-snug mt-0.5"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            Upload one report. <em className="italic">Even an old one.</em>
          </h3>
          <p className="text-[13px] text-slate-500 mt-1.5 leading-relaxed">
            Kindra reads it, explains it in plain English, and remembers it for every future conversation. Most uploads are ready in under a minute.
          </p>
          <div className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#235347]">
            <Sparkle size={12} weight="fill" />
            Pick a file
            <span className="ml-0.5">→</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FirstUploadNudge;
